/**
 * MarkupPanel — the drawing editor's controls, shown in place of the viewer's toolbar while
 * drawing (gallery F-6). It is aria-modal, so the viewer's own key handling (ownsKey) yields to
 * it, and it keeps its own Tab loop.
 *
 * Keys: Escape = cancel (asks first when there are unsaved changes), Ctrl/Cmd+Z = undo,
 * Ctrl/Cmd+Shift+Z or Ctrl+Y = redo, Delete/Backspace = remove the selected mark.
 *
 * "Earlier versions" lists the kept versions (who, when, how many marks), newest first, a page at
 * a time, and restores any of them into the editor; saving then records it as restored. This is
 * what makes "an accidental wipe can be undone" true: a clear keeps the drawing it wiped.
 *
 * The round, translucent icon buttons match the viewer's own dark toolbar (ImageLightbox), which
 * they replace while drawing; Save and Cancel use the shared <Button>.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type ReactNode } from 'react';
import {
  ArrowUpRight,
  Circle,
  History,
  MousePointer2,
  PenLine,
  Redo2,
  RectangleHorizontal,
  Trash2,
  Type,
  Undo2,
  Eraser,
} from 'lucide-react';
import { Button } from '../../../forms/Button';
import { focusableWithin } from '../../../shared/ResponsiveOverlay/useOverlayA11y';
import {
  MARKUP_COLORS,
  MARKUP_TEXT_SIZES,
  MARKUP_WIDTHS,
  MARKUP_LIMITS,
} from '../../../../types/fieldService/generatorPhotoMarkupVocab';
import { isDirty, type MarkupAction, type MarkupEditorState, type MarkupTool } from './markupEditorState';
import { MARKUP_COLOR_HEX } from './markupGeometry';

interface MarkupPanelProps {
  state: MarkupEditorState;
  dispatch: Dispatch<MarkupAction>;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  history: MarkupHistory;
}

export interface MarkupHistoryItem {
  revision: number;
  label: string;
  markCount: number;
  onRestore: () => void;
}

/** The "Earlier versions" list; its data loads only while `open`. */
export interface MarkupHistory {
  open: boolean;
  /** False when the drawing has no kept version yet (never saved, or saved once). */
  available: boolean;
  onToggle: () => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  items: MarkupHistoryItem[];
  hasMore: boolean;
  loadingMore: boolean;
  onMore: () => void;
}

const TOOLS: Array<{ tool: MarkupTool; label: string; icon: ReactNode }> = [
  { tool: 'select', label: 'Select and move', icon: <MousePointer2 className="h-5 w-5" /> },
  { tool: 'arrow', label: 'Arrow', icon: <ArrowUpRight className="h-5 w-5" /> },
  { tool: 'ellipse', label: 'Circle', icon: <Circle className="h-5 w-5" /> },
  { tool: 'freehand', label: 'Freehand', icon: <PenLine className="h-5 w-5" /> },
  { tool: 'text', label: 'Text', icon: <Type className="h-5 w-5" /> },
  { tool: 'textBox', label: 'Text box', icon: <RectangleHorizontal className="h-5 w-5" /> },
];

const ICON_BTN =
  'min-h-[44px] min-w-[44px] p-2 rounded-full transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed';
const iconBtn = (active: boolean) =>
  `${ICON_BTN} ${active ? 'bg-[#007ACC] text-white' : 'bg-white/10 text-white hover:bg-white/20'}`;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function MarkupPanel({ state, dispatch, onSave, onCancel, saving, history }: MarkupPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dirty = isDirty(state);
  const textTool = state.tool === 'text' || state.tool === 'textBox';

  const requestCancel = useCallback(() => {
    if (dirty) setConfirmDiscard(true);
    else onCancel();
  }, [dirty, onCancel]);

  // Focus the tools when the editor opens.
  useEffect(() => {
    rootRef.current?.querySelector<HTMLElement>('[data-tool]')?.focus();
  }, []);

  // Editor keys. The panel is the top-most aria-modal, so the viewer ignores these.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest?.('input, textarea');
      const mod = e.metaKey || e.ctrlKey;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (confirmDiscard) setConfirmDiscard(false);
        else requestCancel();
      } else if (mod && !typing && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
      } else if (mod && !typing && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        dispatch({ type: 'redo' });
      } else if (!typing && (e.key === 'Delete' || e.key === 'Backspace') && state.selectedId) {
        e.preventDefault();
        dispatch({ type: 'deleteSelected' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmDiscard, dispatch, requestCancel, state.selectedId]);

  // Tab stays inside the panel; stopped here so the viewer's own trap never sees it.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !rootRef.current) return;
    e.stopPropagation();
    const f = focusableWithin(rootRef.current);
    if (f.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === f[0]) {
      e.preventDefault();
      f[f.length - 1].focus();
    } else if (!e.shiftKey && active === f[f.length - 1]) {
      e.preventDefault();
      f[0].focus();
    }
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Draw on photo"
      onKeyDown={onKeyDown}
      onClick={(e) => e.stopPropagation()}
      className="shrink-0 z-20 flex flex-col gap-2 bg-black/70 px-3 py-2 text-white"
    >
      <div className="flex flex-wrap items-center gap-1">
        <div role="group" aria-label="Tool" className="flex items-center gap-1">
          {TOOLS.map(({ tool, label, icon }) => (
            <button
              key={tool}
              type="button"
              data-tool={tool}
              aria-label={label}
              title={label}
              aria-pressed={state.tool === tool}
              className={iconBtn(state.tool === tool)}
              onClick={() => dispatch({ type: 'setTool', tool })}
            >
              {icon}
            </button>
          ))}
        </div>

        <span className="mx-1 h-6 w-px bg-white/20" aria-hidden="true" />

        <div role="group" aria-label="Colour" className="flex items-center gap-1">
          {MARKUP_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={cap(color)}
              title={cap(color)}
              aria-pressed={state.color === color}
              onClick={() => dispatch({ type: 'setColor', color })}
              className={`h-8 w-8 min-h-[44px] min-w-[44px] rounded-full flex items-center justify-center ${
                state.color === color ? 'bg-white/25' : 'hover:bg-white/10'
              }`}
            >
              <span className="block h-6 w-6 rounded-full ring-2 ring-white/60" style={{ background: MARKUP_COLOR_HEX[color] }} />
            </button>
          ))}
        </div>

        <span className="mx-1 h-6 w-px bg-white/20" aria-hidden="true" />

        {textTool ? (
          <div role="group" aria-label="Text size" className="flex items-center gap-1">
            {MARKUP_TEXT_SIZES.map((size) => (
              <button key={size} type="button" aria-pressed={state.textSize === size} className={`${iconBtn(state.textSize === size)} text-xs font-bold`} onClick={() => dispatch({ type: 'setTextSize', size })}>
                {size === 'small' ? 'S' : size === 'medium' ? 'M' : 'L'}
                <span className="sr-only"> {size} text</span>
              </button>
            ))}
          </div>
        ) : (
          <div role="group" aria-label="Line width" className="flex items-center gap-1">
            {MARKUP_WIDTHS.map((width) => (
              <button key={width} type="button" aria-label={`${cap(width)} line`} title={`${cap(width)} line`} aria-pressed={state.width === width} className={iconBtn(state.width === width)} onClick={() => dispatch({ type: 'setWidth', width })}>
                <span className="block w-5 rounded-full bg-white" style={{ height: width === 'thin' ? 2 : width === 'medium' ? 4 : 7 }} />
              </button>
            ))}
          </div>
        )}

        <span className="mx-1 h-6 w-px bg-white/20" aria-hidden="true" />

        <button type="button" aria-label="Undo" title="Undo (Ctrl+Z)" className={iconBtn(false)} disabled={state.past.length === 0} onClick={() => dispatch({ type: 'undo' })}>
          <Undo2 className="h-5 w-5" />
        </button>
        <button type="button" aria-label="Redo" title="Redo (Ctrl+Shift+Z)" className={iconBtn(false)} disabled={state.future.length === 0} onClick={() => dispatch({ type: 'redo' })}>
          <Redo2 className="h-5 w-5" />
        </button>
        <button type="button" aria-label="Delete selected mark" title="Delete selected mark" className={iconBtn(false)} disabled={!state.selectedId} onClick={() => dispatch({ type: 'deleteSelected' })}>
          <Trash2 className="h-5 w-5" />
        </button>
        <button type="button" aria-label="Clear all marks" title="Clear all marks (you can undo)" className={iconBtn(false)} disabled={state.shapes.length === 0} onClick={() => dispatch({ type: 'clearAll' })}>
          <Eraser className="h-5 w-5" />
        </button>
        {history.available && (
          <button type="button" aria-label="Earlier versions" title="Earlier versions" aria-expanded={history.open} aria-pressed={history.open} className={iconBtn(history.open)} onClick={history.onToggle}>
            <History className="h-5 w-5" />
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={requestCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onSave} disabled={!dirty} loading={saving} loadingText="Saving...">
            Save drawing
          </Button>
        </div>
      </div>

      {history.open && <HistoryList history={history} restoredFrom={state.restoredFrom} />}

      {textTool && (
        <label className="flex items-center gap-2 text-sm">
          <span className="shrink-0 font-semibold">{state.tool === 'text' ? 'Text, then tap the photo:' : 'Text, then drag a box:'}</span>
          <input
            type="text"
            value={state.text}
            maxLength={MARKUP_LIMITS.maxTextLength}
            onChange={(e) => dispatch({ type: 'setText', text: e.target.value })}
            placeholder="e.g. Leak here"
            className="min-h-[44px] w-full max-w-md rounded-xl bg-white px-3 text-slate-900 ring-1 ring-slate-300 focus:outline-none focus:ring-2 focus:ring-[#3FA5FF]"
          />
        </label>
      )}

      {(state.notice || confirmDiscard) && (
        <div role="status" className="flex flex-wrap items-center gap-2 text-sm">
          {confirmDiscard ? (
            <>
              <span className="font-semibold">Discard your changes to this drawing?</span>
              <Button variant="secondary" size="sm" onClick={() => setConfirmDiscard(false)}>
                Keep drawing
              </Button>
              <Button variant="danger" size="sm" onClick={onCancel}>
                Discard
              </Button>
            </>
          ) : (
            <span className="text-amber-200">{state.notice}</span>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryList({ history, restoredFrom }: { history: MarkupHistory; restoredFrom: number | null }) {
  const marks = (n: number) => (n === 0 ? 'no marks (cleared)' : n === 1 ? '1 mark' : `${n} marks`);
  return (
    <section aria-label="Earlier versions" className="flex max-h-48 flex-col gap-1 overflow-y-auto text-sm">
      <span className="font-semibold">Earlier versions (the last {MARKUP_LIMITS.revisionsKept} are kept)</span>
      {history.loading && <span className="text-white/70">Loading earlier versions...</span>}
      {history.error && (
        <span className="flex items-center gap-2 text-amber-200">
          Couldn't load earlier versions.
          <Button variant="secondary" size="sm" onClick={history.onRetry}>
            Retry
          </Button>
        </span>
      )}
      {!history.loading && !history.error && history.items.length === 0 && (
        <span className="text-white/70">No earlier versions are kept.</span>
      )}
      <ul className="flex flex-col gap-1">
        {history.items.map((item) => (
          <li key={item.revision} className="flex min-h-[44px] items-center gap-2">
            <span className="min-w-0 flex-1 truncate">
              {item.label} - {marks(item.markCount)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={item.onRestore}
              aria-label={`Restore the version by ${item.label}`}
              disabled={restoredFrom === item.revision}
            >
              {restoredFrom === item.revision ? 'Restored' : 'Restore'}
            </Button>
          </li>
        ))}
      </ul>
      {history.hasMore && (
        <Button variant="ghost" size="sm" onClick={history.onMore} loading={history.loadingMore} loadingText="Loading...">
          Show more
        </Button>
      )}
    </section>
  );
}
